import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import { Brand } from "@/store/systemBrand.store";

interface FormData {
	name: string;
	email: string;
	origin: string;
}

interface FormErrors {
	name: string;
	email: string;
	origin: string;
}

interface AddBrandFormProps {
	formData: FormData;
	isEditing: boolean;
	isSubmitting?: boolean;
	handleChange: (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => void;
	handleSubmit: () => void;
	selectedBrand?: Brand;
}

const AddBrandForm: React.FC<AddBrandFormProps> = ({
	formData,
	isEditing,
	isSubmitting = false,
	handleChange,
	handleSubmit,
	selectedBrand
}) => {
	const [errors, setErrors] = useState<FormErrors>({
		name: "",
		email: "",
		origin: ""
	});

	const validateAndSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (!formData.name.trim()) {
			setErrors({ name: "Name is required", email: "", origin: "" });
			return;
		}
		if (!isEditing && !formData.email.trim()) {
			setErrors({ name: "", email: "Email is required", origin: "" });
			return;
		}
		if (!isEditing && !formData.origin) {
			setErrors({
				name: "",
				email: "",
				origin: "Brand origin is required"
			});
			return;
		}
		setErrors({ name: "", email: "", origin: "" });
		handleSubmit();
	};

	useEffect(() => {
		if (selectedBrand) {
			setErrors({ name: "", email: "", origin: "" });
		}
	}, [selectedBrand]);

	return (
		<div className="h-full">
			<form
				onSubmit={validateAndSubmit}
				className="bg-white flex flex-col h-full max-w-md mx-auto rounded-lg"
			>
				<div className="mb-4 flex-1 space-y-4">
					<div className="mb-4">
						<label
							htmlFor="name"
							className="block text-sm font-medium text-gray-700 mb-1"
						>
							Brand Name
						</label>
						<input
							type="text"
							name="name"
							value={formData.name}
							onChange={handleChange}
							placeholder="Enter brand name"
							className="w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
						/>
						{errors.name && (
							<p className="mt-1 text-sm text-red-600">{errors.name}</p>
						)}
					</div>

					{!isEditing && (
						<>
							<div className="mb-4">
								<label
									htmlFor="email"
									className="block text-sm font-medium text-gray-700 mb-1"
								>
									Email
								</label>
								<input
									type="email"
									name="email"
									value={formData.email}
									onChange={handleChange}
									placeholder="Enter email address"
									className="w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
								/>
								{errors.email && (
									<p className="mt-1 text-sm text-red-600">{errors.email}</p>
								)}
							</div>

							<div className="mb-4">
								<label
									htmlFor="name"
									className="block text-sm font-medium text-gray-700 mb-1"
								>
									Brand Origin
								</label>
								<input
									type="text"
									name="origin"
									value={formData.origin}
									onChange={handleChange}
									placeholder="Enter brand origin"
									className="w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
								/>
								{errors.origin && (
									<p className="mt-1 text-sm text-red-600">{errors.origin}</p>
								)}
							</div>
						</>
					)}
				</div>

				<div className="py-4 bg-white mb-[15px]">
					<button
						type="submit"
						className="w-full bg-blue-fynxt text-sm text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
						disabled={isSubmitting}
					>
						{isSubmitting ? (
							<>
								<FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
								{isEditing ? "Updating..." : "Onboarding..."}
							</>
						) : isEditing ? (
							"Update Brand"
						) : (
							"Onboard Brand"
						)}
					</button>
				</div>
			</form>
		</div>
	);
};

export default AddBrandForm;
